import React from 'react'
import { Card, CardContent, Box, Avatar, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Autocomplete } from '@mui/material'
import { Person as PersonIcon } from '@mui/icons-material'
import type { InvoiceFormData } from './types'

interface OptionType {
  value: string | number
  id: number
  label: string
  name: string
  uniqueKey: string
}

interface CustomerInfoCardProps {
  formData: InvoiceFormData
  setFormData: React.Dispatch<React.SetStateAction<InvoiceFormData>>
  accountOptions: OptionType[]
  disabled?: boolean
}

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({ formData, setFormData, accountOptions, disabled }) => {
  return (
    <Card 
      elevation={2} 
      sx={{ 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 3,
          pb: 2,
          borderBottom: '2px solid',
          borderColor: 'success.main'
        }}>
          <Avatar sx={{ 
            bgcolor: 'success.main', 
            width: 40, 
            height: 40,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography 
              variant="h6" 
              color="success.main" 
              fontWeight="600"
              sx={{ fontSize: '1.25rem' }}
            >
              بيانات العميل
            </Typography>
            <Typography variant="body2" color="text.secondary">
              معلومات وتفاصيل العميل
            </Typography>
          </Box>
        </Box>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)'
          }, 
          gap: 3
        }}>
          <Autocomplete
            options={accountOptions}
            getOptionLabel={(option) => String(option.value)}
            value={accountOptions.find(opt => String(opt.value) === formData.customer_code) || null}
            onChange={(event, newValue) => {
              if (newValue) {
                setFormData(prev => ({
                  ...prev,
                  customer_code: String(newValue.value),
                  customer_name: newValue.name
                }))
              } else {
                setFormData(prev => ({
                  ...prev,
                  customer_code: '',
                  customer_name: ''
                }))
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="رمز العميل"
                placeholder="ابحث عن رمز العميل..."
                size="small"
                fullWidth
                disabled={!!disabled}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.uniqueKey}>
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <strong>{option.value}</strong>
                  <small style={{ color: '#666' }}>{option.name}</small>
                </div>
              </li>
            )}
            filterOptions={(options, { inputValue }) => {
              return options
                .filter(option => 
                  option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                  String(option.value).includes(inputValue)
                )
                .slice(0, 20)
            }}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            noOptionsText="لا توجد نتائج"
            clearOnEscape
            blurOnSelect
            disabled={!!disabled}
          />
          <TextField
            label="اسم العميل"
            value={formData.customer_name}
            onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
            fullWidth
            size="small"
            placeholder="أدخل اسم العميل"
            disabled={!!disabled}
          />
          <TextField
            label="رقم الهاتف"
            value={formData.customer_phone}
            onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
            fullWidth
            size="small"
            disabled={!!disabled}
          />
          <FormControl fullWidth size="small" disabled={!!disabled}>
            <InputLabel id="identity-type-label">نوع الهوية</InputLabel>
            <Select
              labelId="identity-type-label"
              value={formData.identity_type}
              label="نوع الهوية"
              onChange={(e) => setFormData(prev => ({ ...prev, identity_type: e.target.value }))}
            >
              <MenuItem value="هوية وطنية">هوية وطنية</MenuItem>
              <MenuItem value="جواز سفر">جواز سفر</MenuItem>
              <MenuItem value="إقامة">إقامة</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="الرقم الضريبي / الهوية"
            value={formData.tax_number}
            onChange={(e) => setFormData(prev => ({ ...prev, tax_number: e.target.value }))}
            fullWidth
            size="small"
            disabled={!!disabled}
          />
        </Box>
      </CardContent>
    </Card>
  )
}

export default CustomerInfoCard
